import { z } from 'zod'

import { MAX_DISCOUNT_BPS } from './codes.ts'

/**
 * Zod input schemas for the guarded catalogue write use-cases. Money is integer
 * grosze, net. The NULL-price ⇔ `isQuoteOnly` pair is validated here whenever
 * BOTH fields are present; the database CHECK (`*_price_quote_xor`) is the
 * authoritative guard for the partial-update case where only one is sent.
 */

const priceQuoteXor = (v: {
  priceNetMinor?: number | null | undefined
  isQuoteOnly?: boolean | undefined
}) =>
  v.priceNetMinor === undefined ||
  v.isQuoteOnly === undefined ||
  (v.priceNetMinor === null) === v.isQuoteOnly

const priceQuoteXorMessage = {
  message: 'priceNetMinor must be null exactly when isQuoteOnly',
  path: ['priceNetMinor'] as PropertyKey[],
}

/**
 * A URL-facing identifier: lowercase, digits and single dashes.
 *
 * Slugs are part of the public address of a thing (`/pl/wynajem#sony-a7-iv`)
 * and are matched exactly when an order resolves a line back to a row, so they
 * are validated rather than sanitised — silently rewriting what the admin typed
 * would make the saved value differ from the one on screen.
 */
/**
 * A code an Admin mints by hand (spec §14 · H1).
 *
 * `value` carries two units and the union is what makes that safe: basis points
 * for `PERCENT`, net grosze for `AMOUNT`, and the refinement below refuses a
 * percentage over 100 rather than leaving the CHECK constraint to say it in
 * SQL somebody has to read a stack trace to see.
 *
 * `isConsultationBonus` is absent on purpose. The one code that stacks with
 * other discounts is earned (spec §8 · I8), not typed into a form.
 */
export const CreateDiscountCodeInput = z
  .object({
    code: z
      .string()
      .trim()
      .min(3)
      .max(64)
      // Letters, digits and dashes. A code with a space in it is a code half
      // the people who receive it will type wrong.
      .regex(/^[A-Za-z0-9-]+$/, 'Kod może zawierać tylko litery, cyfry i myślnik.'),
    kind: z.enum(['PERCENT', 'AMOUNT']),
    /** Basis points for PERCENT (1000 = 10%), net grosze for AMOUNT. */
    value: z.number().int().min(1).max(100_000_000),
    validFrom: z.coerce.date().optional(),
    validUntil: z.coerce.date().optional(),
    maxUses: z.number().int().min(1).max(1_000_000).optional(),
    minBasketNetMinor: z.number().int().min(0).max(100_000_000).optional(),
    /**
     * Which service the code is for. Absent (or null) = the whole shop.
     *
     * Nullable AND optional on purpose: the form sends `null` for „Wszystkie
     * usługi", and a caller that predates the field sends nothing. Both mean
     * the same thing, and making the form send `undefined` for a chosen
     * „wszystko" would be a shape that reads as „I forgot to ask".
     */
    offeringId: z.string().trim().min(1).max(64).nullish(),
  })
  // 99%, not 100% — see `MAX_DISCOUNT_BPS`. A code that zeroes a basket makes an
  // order nobody can pay, which the payment-window job then marks FAILED.
  .refine((v) => v.kind !== 'PERCENT' || v.value <= MAX_DISCOUNT_BPS, {
    message: 'Rabat procentowy nie może przekraczać 99% — koszyka nie wolno wyzerować.',
    path: ['value'],
  })
  .refine((v) => !v.validFrom || !v.validUntil || v.validUntil > v.validFrom, {
    message: 'Koniec ważności musi być po jej początku.',
    path: ['validUntil'],
  })
export type CreateDiscountCodeInput = z.infer<typeof CreateDiscountCodeInput>

export const SlugSchema = z
  .string()
  .trim()
  .min(2)
  .max(64)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Dozwolone są małe litery, cyfry i pojedyncze myślniki.')

/**
 * A ladder — a campaign, or one product's own rungs (spec §14 · H2).
 *
 * `offeringSlug` is what makes it "liczony osobno per produkt": naming one
 * REPLACES the global ladder of that kind for that offering. `MULTI_SERVICE`
 * refuses it, because the bonus is a property of the basket rather than of any
 * line — a per-product basket bonus has no meaning.
 */
export const CreateDiscountLadderInput = z
  .object({
    kind: z.enum(['VOLUME', 'RENTAL_DAYS', 'MULTI_SERVICE']),
    slug: SlugSchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(400).optional(),
    /** Scope it to one product. Absent = the house ladder for its kind. */
    offeringSlug: z.string().trim().min(1).max(120).optional(),
    validFrom: z.coerce.date().optional(),
    validUntil: z.coerce.date().optional(),
  })
  .refine((v) => v.kind !== 'MULTI_SERVICE' || !v.offeringSlug, {
    message: 'Bonus za łączenie dotyczy całego koszyka — nie da się go zawęzić do produktu.',
    path: ['offeringSlug'],
  })
  .refine((v) => !v.validFrom || !v.validUntil || v.validUntil > v.validFrom, {
    message: 'Koniec kampanii musi być po jej początku.',
    path: ['validUntil'],
  })
export type CreateDiscountLadderInput = z.infer<typeof CreateDiscountLadderInput>

/** One rung: "from this many, take this much off". */
export const CreateDiscountStepInput = z.object({
  ladderId: z.string().trim().min(1).max(64),
  threshold: z.number().int().min(1).max(10_000),
  discountBps: z.number().int().min(0).max(10_000),
})
export type CreateDiscountStepInput = z.infer<typeof CreateDiscountStepInput>

const PricingUnitSchema = z.enum(['PIECE', 'HOUR', 'DAY', 'SHOOT_DAY', 'MONTH', 'MINUTE'])
const BookingModeSchema = z.enum(['NONE', 'DATE_RANGE', 'TIME_WINDOW', 'DAY'])
const CoverageModeSchema = z.enum(['NONE', 'ONLINE', 'CITIES'])
const BriefModeSchema = z.enum(['NONE', 'OPTIONAL', 'REQUIRED'])
/** Same three states, about the client's raw material (G4). */
const SourceModeSchema = z.enum(['NONE', 'OPTIONAL', 'REQUIRED'])
/** What a revision request points at — a described element, or a timecode. */
const RevisionAnchorSchema = z.enum(['ELEMENT', 'TIMECODE'])

/**
 * Everything editorial and behavioural about one thing on offer. Price lives on
 * its variants, which is why there is none here.
 *
 * The three capability fields (`pricingUnit`, `bookingMode`, `stockQuantity`)
 * are what make a camera behave like a camera and a montage package like a
 * package — see the block comment in `schema/offerings.ts`. They are editable
 * because turning a one-off into a monthly retainer is an editorial decision,
 * not a migration.
 */
const offeringFields = {
  categoryId: z.string().min(1),
  name: z.string().trim().min(1).max(120),
  kicker: z.string().trim().max(80).nullable(),
  blurb: z.string().trim().max(500).nullable(),
  description: z.string().trim().max(4000).nullable(),
  icon: z.string().trim().max(40).nullable(),
  pricingUnit: PricingUnitSchema,
  bookingMode: BookingModeSchema,
  /** `null` = unlimited; the panel offers a checkbox, not a 0. */
  stockQuantity: z.number().int().min(1).max(9999).nullable(),
  coverageMode: CoverageModeSchema,
  briefMode: BriefModeSchema,
  sourceMode: SourceModeSchema,
  /**
   * `null` = the promise is a DAY rather than a duration, which is the shape
   * montage uses (spec §7). A stored 0 is refused by the column's own CHECK,
   * so the floor is 1 — the panel offers a checkbox for the null.
   *
   * The ceiling is 8760, one year: past that the number is a typo, and a
   * deadline decades out silently disables every overdue sweep that reads it.
   */
  turnaroundHours: z.number().int().min(1).max(8760).nullable(),
  revisionAnchor: RevisionAnchorSchema,
  unitLabel: z.string().trim().max(40).nullable(),
  unitFormOne: z.string().trim().max(40).nullable(),
  unitFormFew: z.string().trim().max(40).nullable(),
  unitFormMany: z.string().trim().max(40).nullable(),
  quantityLabel: z.string().trim().max(120).nullable(),
  defaultQuantity: z.number().int().min(1).max(9999),
  minBookingMinutes: z.number().int().min(1).max(10080).nullable(),
  depositNetMinor: z.number().int().min(1).nullable(),
  capacityLabel: z.string().trim().max(120).nullable(),
  capacityMax: z.number().int().min(1).max(9999).nullable(),
  isVolumeEligible: z.boolean(),
  countsTowardMultiService: z.boolean(),
  isInCreator: z.boolean(),
  sortOrder: z.number().int().min(0),
  isActive: z.boolean(),
}

export const CreateOfferingInput = z.object({
  slug: SlugSchema,
  ...offeringFields,
  kicker: offeringFields.kicker.optional(),
  blurb: offeringFields.blurb.optional(),
  description: offeringFields.description.optional(),
  icon: offeringFields.icon.optional(),
  pricingUnit: PricingUnitSchema.default('PIECE'),
  bookingMode: BookingModeSchema.default('NONE'),
  stockQuantity: offeringFields.stockQuantity.default(null),
  coverageMode: CoverageModeSchema.default('NONE'),
  briefMode: BriefModeSchema.default('NONE'),
  sourceMode: SourceModeSchema.default('NONE'),
  turnaroundHours: offeringFields.turnaroundHours.default(null),
  revisionAnchor: RevisionAnchorSchema.default('ELEMENT'),
  unitLabel: offeringFields.unitLabel.optional(),
  unitFormOne: offeringFields.unitFormOne.optional(),
  unitFormFew: offeringFields.unitFormFew.optional(),
  unitFormMany: offeringFields.unitFormMany.optional(),
  quantityLabel: offeringFields.quantityLabel.optional(),
  defaultQuantity: offeringFields.defaultQuantity.default(1),
  minBookingMinutes: offeringFields.minBookingMinutes.optional(),
  depositNetMinor: offeringFields.depositNetMinor.optional(),
  capacityLabel: offeringFields.capacityLabel.optional(),
  capacityMax: offeringFields.capacityMax.optional(),
  isVolumeEligible: z.boolean().default(false),
  countsTowardMultiService: z.boolean().default(true),
  isInCreator: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
})
export type CreateOfferingInput = z.infer<typeof CreateOfferingInput>

export const UpdateOfferingInput = z.object({
  categoryId: offeringFields.categoryId.optional(),
  name: offeringFields.name.optional(),
  kicker: offeringFields.kicker.optional(),
  blurb: offeringFields.blurb.optional(),
  description: offeringFields.description.optional(),
  icon: offeringFields.icon.optional(),
  pricingUnit: PricingUnitSchema.optional(),
  bookingMode: BookingModeSchema.optional(),
  stockQuantity: offeringFields.stockQuantity.optional(),
  coverageMode: CoverageModeSchema.optional(),
  briefMode: BriefModeSchema.optional(),
  sourceMode: SourceModeSchema.optional(),
  turnaroundHours: offeringFields.turnaroundHours.optional(),
  revisionAnchor: RevisionAnchorSchema.optional(),
  unitLabel: offeringFields.unitLabel.optional(),
  unitFormOne: offeringFields.unitFormOne.optional(),
  unitFormFew: offeringFields.unitFormFew.optional(),
  unitFormMany: offeringFields.unitFormMany.optional(),
  quantityLabel: offeringFields.quantityLabel.optional(),
  defaultQuantity: offeringFields.defaultQuantity.optional(),
  minBookingMinutes: offeringFields.minBookingMinutes.optional(),
  depositNetMinor: offeringFields.depositNetMinor.optional(),
  capacityLabel: offeringFields.capacityLabel.optional(),
  capacityMax: offeringFields.capacityMax.optional(),
  isVolumeEligible: z.boolean().optional(),
  countsTowardMultiService: z.boolean().optional(),
  isInCreator: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
})
export type UpdateOfferingInput = z.infer<typeof UpdateOfferingInput>

/** A priced variant. `durationMinutes` is what tells a calendar how long it is. */
export const CreateVariantInput = z
  .object({
    offeringId: z.string().min(1),
    slug: SlugSchema,
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    priceNetMinor: z.number().int().min(1).nullable(),
    isQuoteOnly: z.boolean(),
    taxRateBps: z.number().int().min(0).max(10000).optional(),
    priceSuffix: z.string().trim().max(40).nullable().optional(),
    durationMinutes: z.number().int().min(1).max(525600).nullable().optional(),
    // What the framework contract's Zalacznik 1 asks a Zlecenie to state, set
    // once per LEVEL and frozen onto every line sold at it. Not amounts, so
    // `offer:update` is the right gate and no new permission is needed.
    revisionRoundsIncluded: z.number().int().min(0).max(10).nullable().optional(),
    exportSpec: z.string().trim().max(500).nullable().optional(),
    thirdPartyMaterials: z.string().trim().max(500).nullable().optional(),
    isPopular: z.boolean().default(false),
    sortOrder: z.number().int().min(0).default(0),
    isActive: z.boolean().default(true),
  })
  .refine(priceQuoteXor, priceQuoteXorMessage)
export type CreateVariantInput = z.infer<typeof CreateVariantInput>

export const UpdateVariantInput = z
  .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().trim().max(500).nullable().optional(),
    priceNetMinor: z.number().int().min(1).nullable().optional(),
    isQuoteOnly: z.boolean().optional(),
    taxRateBps: z.number().int().min(0).max(10000).optional(),
    priceSuffix: z.string().trim().max(40).nullable().optional(),
    durationMinutes: z.number().int().min(1).max(525600).nullable().optional(),
    // What the framework contract's Zalacznik 1 asks a Zlecenie to state, set
    // once per LEVEL and frozen onto every line sold at it. Not amounts, so
    // `offer:update` is the right gate and no new permission is needed.
    revisionRoundsIncluded: z.number().int().min(0).max(10).nullable().optional(),
    exportSpec: z.string().trim().max(500).nullable().optional(),
    thirdPartyMaterials: z.string().trim().max(500).nullable().optional(),
    isPopular: z.boolean().optional(),
    sortOrder: z.number().int().min(0).optional(),
    isActive: z.boolean().optional(),
  })
  .refine(priceQuoteXor, priceQuoteXorMessage)
export type UpdateVariantInput = z.infer<typeof UpdateVariantInput>

/**
 * The variants that include a comparison row. Absence is the "no" — see
 * `FeatureGrants` in `@repo/db/queries/offering`. An empty `valueLabel` is
 * stored as `null` so a stray space cannot render as a qualifier nobody typed.
 */
const FeatureGrantsInput = z.array(
  z.object({
    variantId: z.string().min(1),
    valueLabel: z
      .string()
      .trim()
      .max(120)
      .nullable()
      .transform((v) => v || null),
  }),
)

export const CreateFeatureInput = z.object({
  offeringId: z.string().min(1),
  key: SlugSchema,
  label: z.string().trim().min(1).max(160),
  sortOrder: z.number().int().min(0).default(0),
  grants: FeatureGrantsInput.default([]),
})
export type CreateFeatureInput = z.infer<typeof CreateFeatureInput>

/** `grants` absent edits the label alone and leaves the cells untouched. */
export const UpdateFeatureInput = z.object({
  label: z.string().trim().min(1).max(160).optional(),
  sortOrder: z.number().int().min(0).optional(),
  grants: FeatureGrantsInput.optional(),
})
export type UpdateFeatureInput = z.infer<typeof UpdateFeatureInput>

/**
 * A price-free option a client can ask about. No price field at all, by
 * design — see `QuoteOption` in `mappers.ts`.
 */
export const CreateQuoteOptionInput = z.object({
  offeringId: z.string().min(1),
  slug: SlugSchema,
  label: z.string().trim().min(1).max(160),
  blurb: z.string().trim().max(500).nullable().optional(),
  isCustom: z.boolean().default(false),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
})
export type CreateQuoteOptionInput = z.infer<typeof CreateQuoteOptionInput>

export const UpdateQuoteOptionInput = z.object({
  label: z.string().trim().min(1).max(160).optional(),
  blurb: z.string().trim().max(500).nullable().optional(),
  isCustom: z.boolean().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
})
export type UpdateQuoteOptionInput = z.infer<typeof UpdateQuoteOptionInput>

/**
 * A page of the offer, or a group inside one. `href` and `parentId` are
 * mutually exclusive — a group is a heading on its parent's page, not a route
 * of its own, and the database says so too.
 */
export const CreateCategoryInput = z.object({
  slug: SlugSchema,
  name: z.string().trim().min(1).max(120),
  parentId: z.string().min(1).nullable().default(null),
  href: z.string().trim().max(200).nullable().default(null),
  icon: z.string().trim().max(40).nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
  lead: z.string().trim().max(1000).nullable().optional(),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
})
export type CreateCategoryInput = z.infer<typeof CreateCategoryInput>

export const UpdateCategoryInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  href: z.string().trim().max(200).nullable().optional(),
  icon: z.string().trim().max(40).nullable().optional(),
  title: z.string().trim().max(200).nullable().optional(),
  lead: z.string().trim().max(1000).nullable().optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
})
export type UpdateCategoryInput = z.infer<typeof UpdateCategoryInput>

/**
 * Coordinates are WGS84 degrees and are range-checked here because a swapped
 * pair still parses as two numbers — it just puts Wrocław in the ocean.
 */
export const CreateCityInput = z.object({
  slug: SlugSchema,
  name: z.string().trim().min(1).max(120),
  note: z.string().trim().max(200).nullable().optional(),
  longitude: z.number().min(-180).max(180),
  latitude: z.number().min(-90).max(90),
  sortOrder: z.number().int().min(0).default(0),
  isActive: z.boolean().default(true),
})
export type CreateCityInput = z.infer<typeof CreateCityInput>

export const UpdateCityInput = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  note: z.string().trim().max(200).nullable().optional(),
  longitude: z.number().min(-180).max(180).optional(),
  latitude: z.number().min(-90).max(90).optional(),
  sortOrder: z.number().int().min(0).optional(),
  isActive: z.boolean().optional(),
})
export type UpdateCityInput = z.infer<typeof UpdateCityInput>

/** The whole coverage list, replaced in one write. */
export const SetOfferingCitiesInput = z.object({
  offeringId: z.string().min(1),
  cityIds: z.array(z.string().min(1)).max(200),
})
export type SetOfferingCitiesInput = z.infer<typeof SetOfferingCitiesInput>

export const UpdateDiscountStepInput = z.object({
  threshold: z.number().int().min(1).max(9999).optional(),
  discountBps: z.number().int().min(0).max(10000).optional(),
})
export type UpdateDiscountStepInput = z.infer<typeof UpdateDiscountStepInput>
